/**
 * Removes trailing slashes from a URL string (e.g. for canonical URLs).
 */
export function removeTrailingBackslash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Normalizes a link href so it has no trailing slash (for nav/footer links).
 * Preserves '#' and root '/' as-is.
 */
export function normalizeLinkHref(url: string): string {
  if (!url || url.startsWith('#') || url.length === 1) return url;
  return removeTrailingBackslash(url) ?? '/';
}

/**
 * Returns the public/canonical site URL for the current environment.
 * Used for canonical tags, sitemap, structured data, etc.
 * In production, CLIENT_URL must be set (e.g. https://www.lend.com.au) so canonicals
 * are correct when HTML is generated (including during revalidation from ALB).
 */
export function getDomain(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Use explicit public site URL from env so prod canonicals are never the staging host
  if (process.env.CLIENT_URL) {
    return removeTrailingBackslash(process.env.CLIENT_URL);
  }
  if (process.env.NODE_ENV === 'development') {
    return 'https://staging-www.lend.com.au';
  }
  return 'https://www.lend.com.au';
}

/** Category shape with optional recursive ParentCategory (Strapi manyToOne or oneToMany). */
type CategoryLike = {
  Slug?: string;
  ParentCategory?: CategoryLike | CategoryLike[] | null;
};

/**
 * Build the full category path (root to leaf) by traversing ParentCategory.
 */
function getCategoryPathSegments(category: CategoryLike): string[] {
  const slugs: string[] = [];
  let cat: CategoryLike | null | undefined = category;
  const maxDepth = 5;
  let depth = 0;
  while (cat?.Slug && depth < maxDepth) {
    slugs.unshift(cat.Slug);
    const parent: CategoryLike | CategoryLike[] | null = cat.ParentCategory ?? null;
    cat = parent == null ? null : Array.isArray(parent) ? parent[0] : parent;
    depth++;
  }
  return slugs;
}

/**
 * Generate a page URL including the full recursive page category path.
 * @param page - The page object with Slug and optional PageCategory (with ParentCategory chain)
 * @returns The URL path for the page (e.g. /parent-cat/cat/page-slug or /page-slug)
 */
export function getPageUrl(page: { Slug?: string; PageCategory?: CategoryLike | null }): string {
  if (!page?.Slug) return '/';

  if (page.PageCategory?.Slug) {
    const segments = getCategoryPathSegments(page.PageCategory);
    const path = segments.length ? `/${segments.join('/')}/${page.Slug}` : `/${page.Slug}`;
    return path === '/home' || path === '/index' ? '/' : path;
  }

  return `/${page.Slug}`;
}

/**
 * Check if a path looks like an asset file (JS chunks, CSS, images, etc.)
 * These should be served as static files, not routed through page components
 *
 * @param path - The path to check (e.g., '/chunks/123.js' or 'chunks/123.js')
 * @param firstSegment - Optional first segment of the path for faster checking
 * @returns true if the path appears to be an asset file
 */
export function isAssetFile(path: string, firstSegment?: string): boolean {
  // Normalize path (remove leading slash if present for consistent checking)
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  const segment = firstSegment || normalizedPath.split('/')[0];

  // Never block paths under /assets/ or /public/ - these are our static files in public/
  // (Next.js serves public/ at the root, so /assets/logo-color.svg is valid)
  if (segment === 'assets' || normalizedPath.startsWith('assets/')) {
    return false;
  }

  // Allow SEO XML (sitemap.xml, sitemap-0.xml etc.) so they reach Next.js and are not 404'd as "asset"
  if (normalizedPath === 'sitemap.xml' || /^sitemap(-[a-z0-9]+)?\.xml$/i.test(normalizedPath)) {
    return false;
  }

  return (
    // File extensions (bot/crawler requests for random .js, .css etc. that aren't under /assets/)
    normalizedPath.match(
      /\.(js|css|map|woff|woff2|ttf|eot|svg|png|jpg|jpeg|gif|webp|ico|php|jar|webmanifest|txt|xml)$/i
    ) !== null ||
    // Next.js internal paths
    segment === '_next' ||
    normalizedPath.startsWith('_next/') ||
    // Chunk patterns
    segment === 'chunks' ||
    normalizedPath.startsWith('chunks/') ||
    normalizedPath.startsWith('page-') ||
    normalizedPath.startsWith('app/') ||
    normalizedPath.startsWith('webpack-') ||
    // Other common asset patterns
    normalizedPath.startsWith('static/') ||
    // URL-encoded paths (like %2BCSCOE%2B)
    normalizedPath.includes('%')
  );
}

/**
 * Check if a path matches known invalid URL patterns that should return 404
 * These are typically bot requests, old links, or malicious attempts
 *
 * @param path - The path to check (e.g., '/Core/Skin/Login.aspx')
 * @returns true if the path matches an invalid pattern
 */
export function isInvalidUrl(path: string): boolean {
  // Normalize path (remove leading slash if present)
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return false; // Root path is valid
  }

  // Check for suspicious file extensions in URL segments (not assets)
  const suspiciousExtensions = [
    '.aspx',
    '.asp',
    '.jsp',
    '.cfm',
    '.cgi',
    '.pl',
    '.py',
    '.rb',
    '.exe',
    '.dll',
    '.bat',
    '.cmd',
    '.sh',
    '.sql',
    '.bak',
    '.old',
    '.tmp',
    '.log',
  ];

  // Check if any segment ends with a suspicious extension
  for (const segment of segments) {
    const lowerSegment = segment.toLowerCase();
    if (suspiciousExtensions.some((ext) => lowerSegment.endsWith(ext))) {
      return true;
    }
  }

  // Check for common invalid path patterns
  const invalidPatterns = [
    // ASP.NET paths
    /^(Core|App_Data|App_Code|bin|obj)\//i,
    // Common bot/scanner paths
    /^(admin|administrator|wp-admin|wp-content|wp-includes|phpmyadmin|mysql|sql|database)\//i,
    // Old CMS paths
    /^(cms|content|includes|lib|libs|scripts|styles|css|js|images|img|assets)\//i,
    // Suspicious directory patterns
    /^(config|conf|settings|setup|install|update|upgrade|backup|backups)\//i,
    // Windows system paths (if somehow encoded)
    /^(Windows|System32|Program Files|ProgramData)\//i,
  ];

  for (const pattern of invalidPatterns) {
    if (pattern.test(normalizedPath)) {
      return true;
    }
  }

  // Check for paths with too many segments (likely invalid)
  // Our site structure typically has max 3-4 segments (category/category/page)
  // More than 6 segments is suspicious
  if (segments.length > 6) {
    return true;
  }

  return false;
}
